import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Message, Model, Parameter } from '@shared/types';
import { ModelConfig } from '../types/misc.ts';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const UUID_RE =
  /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

/** Generates a human-readable editor URL: /editor/title-slug-uuid */
export function editorUrl(conversationId: string, title?: string): string {
  if (!title || title === 'New Conversation' || title === 'Loading…') {
    return `/editor/${conversationId}`;
  }
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 60);
  return `/editor/${slug}-${conversationId}`;
}

/** Extracts the UUID from an editor URL param (handles both slug-uuid and plain uuid). */
export function extractEditorId(param: string): string {
  const match = param.match(UUID_RE);
  return match ? match[1] : param;
}

/**
 * Validates and sanitizes a redirect URL to prevent open redirect attacks
 * Only allows relative paths or same-origin URLs
 * @param redirectUrl - The URL to validate
 * @param fallback - Fallback URL if validation fails (default: '/')
 * @returns A safe redirect URL
 */
export function validateRedirectUrl(
  redirectUrl: string | null,
  fallback: string = '/',
): string {
  // If no redirect URL provided, return fallback
  if (!redirectUrl) {
    return fallback;
  }

  try {
    // Decode the URL in case it was encoded
    const decodedUrl = decodeURIComponent(redirectUrl);

    // Check if it's a relative path (starts with /)
    if (decodedUrl.startsWith('/') && !decodedUrl.startsWith('//')) {
      // Additional check to prevent protocol-relative URLs (//example.com)
      // Remove any query parameters that could contain malicious data
      const url = new URL(decodedUrl, window.location.origin);

      // Ensure it's still on the same origin after URL parsing
      if (url.origin === window.location.origin) {
        return url.pathname + url.search + url.hash;
      }
    }

    // Check if it's a same-origin absolute URL
    const url = new URL(decodedUrl);
    if (url.origin === window.location.origin) {
      return url.pathname + url.search + url.hash;
    }

    // If we get here, it's an external URL or invalid - return fallback
    console.warn('Rejected redirect URL (external or invalid):', redirectUrl);
    return fallback;
  } catch (error) {
    // Invalid URL format - return fallback
    console.warn('Invalid redirect URL format:', redirectUrl, error);
    return fallback;
  }
}

/**
 * Server-side version of validateRedirectUrl for use in server components and API routes
 * @param redirectUrl - The URL to validate
 * @param requestOrigin - The origin from the request headers
 * @param fallback - Fallback URL if validation fails (default: '/')
 * @returns A safe redirect URL
 */
export function validateRedirectUrlServer(
  redirectUrl: string | null,
  requestOrigin: string | null,
  fallback: string = '/',
): string {
  // If no redirect URL or origin provided, return fallback
  if (!redirectUrl || !requestOrigin) {
    return fallback;
  }

  try {
    // Decode the URL in case it was encoded
    const decodedUrl = decodeURIComponent(redirectUrl);

    // Check if it's a relative path (starts with /)
    if (decodedUrl.startsWith('/') && !decodedUrl.startsWith('//')) {
      // Additional check to prevent protocol-relative URLs (//example.com)
      // Remove any query parameters that could contain malicious data
      const url = new URL(decodedUrl, requestOrigin);

      // Ensure it's still on the same origin after URL parsing
      if (url.origin === requestOrigin) {
        return url.pathname + url.search + url.hash;
      }
    }

    // Check if it's a same-origin absolute URL
    const url = new URL(decodedUrl);
    if (url.origin === requestOrigin) {
      return url.pathname + url.search + url.hash;
    }

    // If we get here, it's an external URL or invalid - return fallback
    console.warn('Rejected redirect URL (external or invalid):', redirectUrl);
    return fallback;
  } catch (error) {
    // Invalid URL format - return fallback
    console.warn('Invalid redirect URL format:', redirectUrl, error);
    return fallback;
  }
}

export function updateParameter(code: string, param: Parameter): string {
  const escapedName = escapeRegExp(param.name);

  const isPython = /^from build123d\b|^import math\b|^with BuildPart/m.test(
    code,
  );

  if (isPython) {
    // Python: name = value   # optional comment
    const regex = new RegExp(
      `^(\\s*${escapedName}\\s*=\\s*)[^#\\n]+?([\\t ]*(?:#[^\\n]*)?)$`,
      'm',
    );
    switch (param.type) {
      case 'string':
        return code.replace(
          regex,
          `$1"${escapeReplacement(escapeQuotes(param.value as string))}"$2`,
        );
      case 'boolean':
        // Python capitalises boolean literals
        return code.replace(regex, `$1${param.value ? 'True' : 'False'}$2`);
      default:
        return code.replace(regex, `$1${param.value}$2`);
    }
  }

  // OpenSCAD: name = value;  // optional comment
  const regex = new RegExp(
    `^\\s*(${escapedName}\\s*=\\s*)[^;]+;([\\t\\f\\cK ]*\\/\\/[^\n]*)?`,
    'm',
  );
  if (!param.type) {
    return code.replace(regex, `$1${param.value};$2`);
  }
  switch (param.type) {
    case 'string':
      return code.replace(
        regex,
        `$1"${escapeReplacement(escapeQuotes(param.value as string))}";$2`,
      );
    case 'number':
      return code.replace(regex, `$1${param.value};$2`);
    case 'boolean':
      return code.replace(regex, `$1${param.value};$2`);
    case 'string[]':
      return code.replace(
        regex,
        `$1[${(param.value as string[])
          .map((value) => escapeReplacement(escapeQuotes(value)))
          .map((value) => `"${value}"`)
          .join(',')}];$2`,
      );
    case 'number[]':
      return code.replace(
        regex,
        `$1[${(param.value as number[]).join(',')}];$2`,
      );
    case 'boolean[]':
      return code.replace(
        regex,
        `$1[${(param.value as boolean[]).join(',')}];$2`,
      );
    default:
      return code;
  }
}

export function getDiffString(param: Parameter) {
  let diffString: string = '';
  let diffNumber: number = 0;
  // Default to assuming the type is number
  if (!param.type) {
    diffNumber =
      Math.round((Number(param.value) - Number(param.defaultValue)) * 10) / 10;
    diffString = diffNumber > 0 ? `+${diffNumber}` : `${diffNumber}`;
    return diffString;
  }
  switch (param.type) {
    case 'number':
      diffNumber =
        Math.round((Number(param.value) - Number(param.defaultValue)) * 10) /
        10;
      diffString = diffNumber > 0 ? `+${diffNumber}` : `${diffNumber}`;
      break;
    case 'boolean':
      diffString = param.value ? 'true' : 'false';
      break;
    case 'string':
      diffString = param.value as string;
      break;
    case 'string[]':
      diffString = (param.value as string[])
        .map((value, index) => {
          if (value !== (param.defaultValue as string[])[index]) {
            return value;
          }
        })
        .filter((value) => value !== undefined)
        .join('\n');
      break;
    case 'number[]':
      diffString = (param.value as number[])
        .map((value, index) => {
          const diffNumber =
            Math.round(
              (Number(value) -
                Number((param.defaultValue as number[])[index])) *
                10,
            ) / 10;
          if (diffNumber !== 0) {
            return diffNumber > 0 ? `+${diffNumber}` : `${diffNumber}`;
          }
        })
        .filter((value) => value !== undefined)
        .join('\n');
      break;
    case 'boolean[]':
      diffString = (param.value as boolean[])
        .map((value, index) => {
          if (value !== (param.defaultValue as boolean[])[index]) {
            return value ? 'true' : 'false';
          }
        })
        .filter((value) => value !== undefined)
        .join('\n');
      break;
    default:
      diffString = '';
  }
  return diffString;
}

export function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

export function escapeReplacement(string: string) {
  return string.replace(/\$/g, '$$$$');
}

export function escapeQuotes(string: string) {
  return string.replace(/"/g, '\\"');
}

export function getInitials(fullName: string | null) {
  if (fullName) {
    return fullName
      .split(' ')
      .map((n: string) => n[0])
      .join('')
      .toUpperCase();
  }
  return 'U';
}

export const PARAMETRIC_MODELS: ModelConfig[] = [
  {
    id: 'anthropic/claude-fable-5',
    name: 'Claude Fable 5',
    shortName: 'Fable',
    description:
      "Anthropic's most powerful frontier model — a tier above Opus. Use for the hardest parts, long-horizon agentic builds, and the most demanding visual reasoning.",
    provider: 'Anthropic',
    supportsTools: true,
    supportsThinking: true,
    supportsVision: true,
  },
  {
    id: 'anthropic/claude-opus-4.8',
    name: 'Claude Opus 4.8',
    shortName: 'Claude',
    description:
      "Anthropic's world-leading coding model with HD vision. Use for python script defined structures, and strong visual analysis.",
    provider: 'Anthropic',
    supportsTools: true,
    supportsThinking: true,
    supportsVision: true,
  },
  {
    id: 'openai/gpt-5.5',
    name: 'GPT-5.5',
    shortName: 'GPT',
    description:
      "OpenAI's fast, hands-off workhorse. Use for simplicity, speed, and reliability.",
    provider: 'OpenAI',
    supportsTools: true,
    supportsThinking: true,
    supportsVision: true,
  },
  {
    id: 'google/gemini-3.1-pro-preview',
    name: 'Gemini 3.1 Pro',
    shortName: 'Gemini',
    description:
      "Google's multimodal high token context, long-horizon task model. Use for large, tedious tasks, and schematic-to-CAD.",
    provider: 'Google',
    supportsTools: true,
    supportsThinking: true,
    supportsVision: true,
  },
  {
    id: 'moonshotai/kimi-k2.6',
    name: 'Kimi K2.6',
    shortName: 'Kimi',
    description:
      "Moonshot AI's affordable, user configurable, multi agent underdog. Use for customizable experimentation.",
    provider: 'MoonshotAI',
    supportsTools: true,
    supportsThinking: true,
    supportsVision: true,
  },
];

export const IMAGE_TO_CAD_MODELS: ModelConfig[] = [
  {
    id: 'trellis2',
    name: 'Magneto 0.1',
    family: 'Magneto',
    maxImages: 99,
    description:
      'Textured PBR mesh — sharp features and transparency. The default tier.',
    timeEstimate: '1-2 minutes',
  },
  {
    id: 'magneto4b',
    name: 'Magneto 0.2',
    family: 'Magneto',
    maxImages: 1,
    description:
      'Self-hosted pixel-aligned image-to-3D — segmented, CAD-ready output.',
    timeEstimate: '2-4 minutes',
  },
  {
    id: 'meshy7',
    name: 'Magneto 0.3 (Beta)',
    family: 'Magneto',
    maxImages: 4,
    description:
      'Newest engine — most accurate geometry and highest-fidelity texturing.',
    timeEstimate: '3-5 minutes',
  },
  {
    id: 'trellis2alias',
    name: 'Trellis 2',
    family: 'Trellis',
    maxImages: 99,
    description: 'Same engine as Magneto 0.1 — textured PBR mesh.',
    timeEstimate: '1-2 minutes',
  },
  {
    id: 'trellis1',
    name: 'Trellis 1',
    family: 'Trellis',
    maxImages: 99,
    description: 'Original TRELLIS — single or multi-image, lightweight mesh.',
    timeEstimate: '~1 minute',
  },
  {
    id: 'pixal3d',
    name: 'Pixal3D',
    maxImages: 1,
    description: 'Pixel-aligned image-to-3D on fal — single image.',
    timeEstimate: '1-2 minutes',
  },
  {
    id: 'hunyuan',
    name: 'Hunyuan v3.1 Pro',
    family: 'Hunyuan',
    maxImages: 8,
    description: 'Up to 8 views, ~1.5M faces, PBR.',
    timeEstimate: '1-2 minutes',
  },
  {
    id: 'hunyuan_rapid',
    name: 'Hunyuan v3.1 Rapid',
    family: 'Hunyuan',
    maxImages: 1,
    description: 'Fast single-image — no multi-view, fixed poly.',
    timeEstimate: '~30 seconds',
  },
  {
    id: 'hunyuan_v3',
    name: 'Hunyuan v3',
    family: 'Hunyuan',
    maxImages: 4,
    description: 'Up to 4 views, ~1.5M faces, PBR.',
    timeEstimate: '1-2 minutes',
  },
  {
    id: 'hunyuan_v2',
    name: 'Hunyuan v2',
    family: 'Hunyuan',
    maxImages: 3,
    description: 'Single or 3-view (front/back/left), textured mesh.',
    timeEstimate: '1-2 minutes',
  },
  {
    id: 'hunyuan_v2_mini',
    name: 'Hunyuan v2 Mini',
    family: 'Hunyuan',
    maxImages: 1,
    description: 'Single image, smaller/faster model.',
    timeEstimate: '~30 seconds',
  },
  {
    id: 'ultra',
    name: 'Max Quality',
    family: 'Meshy',
    maxImages: 1,
    description: 'Highest quality mesh and clean topology.',
    timeEstimate: '5-6 minutes',
  },
  {
    id: 'meshy6',
    name: 'Meshy v6',
    family: 'Meshy',
    maxImages: 4,
    description: 'Single or multi-image (up to 4), PBR, rigging, animation.',
    timeEstimate: '3-5 minutes',
  },
  {
    id: 'meshy5',
    name: 'Meshy v5',
    family: 'Meshy',
    maxImages: 4,
    description: 'Multi-image only (1-4 images), PBR, rigging.',
    timeEstimate: '3-5 minutes',
  },
  {
    id: 'tripo',
    name: 'Tripo v2.5',
    family: 'Tripo',
    maxImages: 4,
    description: 'Fast textured PBR mesh; supports multi-view (up to 4).',
    timeEstimate: '~1 minute',
  },
  {
    id: 'tripo_h31',
    name: 'Tripo H3.1',
    family: 'Tripo',
    maxImages: 4,
    description: 'High-fidelity hero tier; single or multi-view (2-4).',
    timeEstimate: '1-2 minutes',
  },
  {
    id: 'tripo_p1',
    name: 'Tripo P1',
    family: 'Tripo',
    maxImages: 1,
    description: 'Single image, clean low-poly game-ready topology.',
    timeEstimate: '~1 minute',
  },
  {
    id: 'triposr',
    name: 'TripoSR (Legacy)',
    family: 'Tripo',
    maxImages: 1,
    description: 'Legacy open-source TripoSR — single image, basic GLB/OBJ.',
    timeEstimate: '~30 seconds',
  },
  {
    id: 'quality',
    name: 'Draft',
    maxImages: 1,
    description: 'Rough quality for quick iterations.',
    timeEstimate: '~45 seconds',
  },
  {
    id: 'fast',
    name: 'Textureless',
    maxImages: 1,
    description: 'Faster, with simpler, textureless output.',
    timeEstimate: '60-90 seconds',
  },
];

// Whether the selected parametric model can accept image / STL-render inputs.
// Unknown ids (e.g. historical messages tagged with a removed model) fall back
// to `true` so legacy conversations keep rendering normally.
export function parametricModelSupportsVision(modelId: string): boolean {
  const cfg = PARAMETRIC_MODELS.find((m) => m.id === modelId);
  return cfg?.supportsVision !== false;
}

export function getBackupModel({
  message,
  parentMessage,
  type,
}: {
  message: Message;
  parentMessage?: Message;
  type: 'parametric' | 'imagetocad';
}): Model {
  if (message.content.model) {
    return message.content.model;
  }
  if (parentMessage?.content.model) {
    return parentMessage.content.model;
  }
  if (type === 'parametric') {
    // A parametric conversation must fall back to a real OpenRouter model id —
    // not a creative tier like 'fast' (which is a IMAGE_TO_CAD_MODELS label and an
    // invalid model id for the parametric pipeline).
    return PARAMETRIC_MODELS[0].id;
  } else {
    return 'quality';
  }
}
